<script lang="ts">
	import {
		AdvancedCard,
		Alert,
		Button,
		Container,
		IconButton,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import { compare } from 'compare-versions';
	import { rebootDevice } from 'src/helpers/device_settings_helpers';
	import { is_GBLE, isWallDisplay, macFromId, macToID, real_id } from 'src/helpers/interface_helpers';
	import {
		addToObserverList,
		removeFromObserverList,
		getObserverLists,
	} from 'src/helpers/observer_lists_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { cachedLists } from 'src/stores/device-runtime/firmware-ui';
	import {
		isGatewayObserver,
		isFwAfter200,
		removeCachedBTHomeComponents,
		removedBTHomeControl,
	} from 'src/helpers/ble_devices_helpers';
	import { btHomeRemoteIcon } from 'src/stores/features/ble/bt-home-remote-icon';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';

	export let device;

	const deviceID = real_id(device.id);

	$: statusStore = getDeviceStatus(deviceID);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(deviceID);
	let settings = readDeviceSettings(deviceID);
	$: settings = $settingsStore ?? settings;

	$: isGatewayAlwaysEnabled =
		isFwAfter200(settings) || // FW 2.0.0+: BLE gateway is always available, observer is automatic
		typeof settings?.ble?.observer?.always_on !== 'undefined' || // after 1.5.0 gateway option is changed, after this change there is always_on param, so the gateway is always enabled. Also inside local web this option is missing
		typeof status?.sys?.btrelay_rev !== 'undefined'; // after 1.5.0 gateway fw version is added - btrelay_rev

	const fwVersion = (
		settings?.DeviceInfo?.ver ||
		settings?.sys?.device.fw_id ||
		settings?.DeviceInfo?.fw_id
	)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0];

	$: isOnline = status?.online;

	const isBeforeOneFive = fwVersion ? compare(fwVersion, '1.5.0', '<') : true;

	$: isProxyingAvailable = enable && (isObserver || isGatewayAlwaysEnabled) && isOnline;

	let showModal = false;
	let deviceName = '';
	let deviceMac = '';
	let enable = isFwAfter200(settings) || settings?.ble?.enable;
	let isObserver = isGatewayObserver(device);
	let showAddModal = false;
	let selectedDevicesWithMac = {};

	$: showAssignedDevices =
		connectedDevices.length > 0 || (enable && (isObserver || isGatewayAlwaysEnabled));

	$: existingBleDevices = Object.values($devices).filter((device) => is_GBLE(device));

	// Handle multi-channel devices
	$: uniqueChannelDevices = Object.values(
		existingBleDevices.reduce((uniqueDevices, device) => {
			// Extract the base device ID without channel suffix
			const baseId = device.id.split('_')[0];

			// If we haven't seen this base device yet, or if this is channel 0,
			// add it to our list
			if (!uniqueDevices[baseId] || device.channel === 0) {
				uniqueDevices[baseId] = device;
			}

			return uniqueDevices;
		}, {}),
	);

	$: availableBleDevices = uniqueChannelDevices.filter((device: Device) => {
		// Filter out offline GBLE devices
		if (!status?.online) return false;

		// Filter out shared devices
		if (device.shared) return false;

		// Filter out already connected devices
		if (
			connectedDevices.some(
				(connectedDevice) =>
					(typeof connectedDevice === 'string' && connectedDevice === device.id) ||
					(typeof connectedDevice !== 'string' && connectedDevice.id === device.id),
			)
		)
			return false;

		return true;
	});

	$: list = Object.values(
		availableBleDevices.reduce((roomGroups, device: Device) => {
			const roomID = device.room_id;
			const roomName =
				$rooms[roomID]?.name || capFirstChar($format('label_hidden', { default: 'Hidden' }));

			if (!roomGroups[roomID]) {
				roomGroups[roomID] = {
					cards: [],
					roomName: roomName,
					roomID: roomID,
				};
			}
			roomGroups[roomID].cards.push({
				name:
					device.name ||
					$format('title_unknown_ble_device', {
						default: 'Unknown BLE Device',
					}),
				id: device.id,
				selected: !!selectedDevicesWithMac[device.id],
				roomName: roomName,
				roomID: roomID,
				...getDeviceAvatar(device),
			});

			return roomGroups;
		}, {}),
	);

	$: selectedDevicesCount = Object.keys(selectedDevicesWithMac).length;

	$: deviceMacAddressses = $cachedLists[real_id(device?.id)]?.['ble.cloudrelay.list']?.addrs || [];

	$: connectedDevices = deviceMacAddressses
		.map((mac) => {
			if ($devices[macToID(mac)]) {
				return { ...$devices[macToID(mac)], addr: mac };
			} else {
				return mac;
			}
		})
		.sort((a, _) => (typeof a === 'object' ? -1 : 1));

	const selectDeviceCard = (device) => {
		const deviceId = device.id;

		// Get device MAC address
		const deviceMacAddress = (device?.mac || macFromId(device.id) || '')
			.toLowerCase()
			.match(/.{1,2}/g);
		const mac = device?.addr || (deviceMacAddress ? deviceMacAddress.join(':') : '');

		// Check if MAC exists
		if (!mac) {
			toast.open({
				type: 'warning',
				title: $format('toast_warning_missing_mac', {
					default: "This device doesn't have a MAC address and cannot be added to the gateway",
				}),
			});
			return;
		}

		const mapCopy = { ...selectedDevicesWithMac };

		// Toggle the selection
		if (mapCopy[deviceId]) {
			delete mapCopy[deviceId];
		} else {
			mapCopy[deviceId] = mac;
		}

		selectedDevicesWithMac = mapCopy;
	};

	const addSelectedDevices = async () => {
		const selectedMacs = Object.values(selectedDevicesWithMac);
		if (selectedMacs.length === 0) return;

		let successCount = 0;
		let failCount = 0;

		for (const mac of selectedMacs) {
			try {
				const res = await addToObserverList(device, mac);

				if (res?.isok) {
					cachedLists.update((devicesList) => {
						const deviceId = real_id(device?.id);

						if (!devicesList[deviceId]) {
							devicesList[deviceId] = {};
						}

						if (!devicesList[deviceId]['ble.cloudrelay.list']) {
							devicesList[deviceId]['ble.cloudrelay.list'] = { addrs: [] };
						}

						// Check if we need to add this MAC
						const currentDeviceMacs =
							devicesList[deviceId]['ble.cloudrelay.list'].addrs || [];
						if (!currentDeviceMacs.includes(mac)) {
							devicesList[deviceId]['ble.cloudrelay.list'].addrs = [
								...currentDeviceMacs,
								mac,
							];
						}

						return devicesList;
					});

					successCount++;
				} else {
					failCount++;
				}
			} catch (error) {
				failCount++;
			}
		}

		if (successCount > 0) {
			if (!isWallDisplay(device.type)) {
				rebootDevice(device);
			}

			if (failCount === 0) {
				toast.open({
					type: 'success',
					title: $format('toast_success_added_devices', {
						default: `Successfully added ${successCount} device${successCount > 1 ? 's' : ''}`,
						values: { count: successCount },
					}),
				});
			} else {
				toast.open({
					type: 'warning',
					title: $format('toast_partial_success_added_devices', {
						default: `Added ${successCount} device${successCount > 1 ? 's' : ''}, but failed to add ${failCount}`,
						values: { successCount, failCount },
					}),
				});
			}
		} else {
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
		}

		// Clean up
		closeAddModal();
	};

	const getDeviceAvatar = (item) => {
		if (typeof item !== 'string') {
			if (item?.image) {
				return { image: item.image };
			} else if (item?.icon) {
				return { icon: item.icon };
			} else if (item?.backgroundColor) {
				return { backgroundColor: item.backgroundColor };
			}
		}
		return { image: 'images/device_images/unknown-device.svg' };
	};

	const handleDeleteDevice = (e, item) => {
		e.stopPropagation();
		showModal = true;
		deviceName =
			typeof item !== 'string'
				? item?.name
				: $format('span_unknown_ble_device_with_mac', {
						default: 'unknown BLE device with MAC address {macAddress}',
						values: { macAddress: item },
					});
		deviceMac = typeof item !== 'string' ? item?.addr : item;
	};

	const closeRemoveModal = () => {
		showModal = false;
		deviceName = '';
		deviceMac = '';
	};

	const removeDevice = async () => {
		const res = await removeFromObserverList(device, deviceMac);
		if (res?.isok) {
			if (
				$cachedLists?.[real_id(device?.id)]?.['ble.cloudrelay.list']?.addrs?.includes(
					deviceMac,
				) &&
				!isWallDisplay(device.type)
			) {
				rebootDevice(device);
			}

			cachedLists.update((oldList) => {
				const currentDeviceMacs = oldList[real_id(device?.id)]['ble.cloudrelay.list']?.addrs;
				const filteredDeviceMacs = currentDeviceMacs.filter((mac) => mac !== deviceMac);
				if (oldList[real_id(device?.id)]) {
					oldList[real_id(device?.id)]['ble.cloudrelay.list'].addrs = filteredDeviceMacs;
				}
				return oldList;
			});

			const removedKeys = removeCachedBTHomeComponents(deviceID, deviceMac);
			if (removedBTHomeControl(removedKeys)) {
				btHomeRemoteIcon.clearRemotes(deviceID);
			}

			toast.open({
				type: 'success',
				title: $format('toast_success_deleted_device', {
					default: 'Successfully deleted device',
				}),
			});
		} else {
			toast.open({ type: 'danger', title: $format('error_global') });
		}
		closeRemoveModal();
	};

	const handleDeviceSelection = (e: CustomEvent) => {
		const deviceId = (e.detail as { id?: string })?.id;
		const device = availableBleDevices.find((d: Device) => d.id === deviceId);
		if (device) {
			selectDeviceCard(device);
		}
	};

	const closeAddModal = () => {
		showAddModal = false;
		deviceName = '';
		deviceMac = '';
		selectedDevicesWithMac = {};
	};

	const fetchCloudRelayList = async () => {
		try {
			const res = await getObserverLists(device);
			if (res?.isok && res?.data?.addrs) {
				cachedLists.update((lists) => {
					if (!lists[deviceID]) lists[deviceID] = {};
					lists[deviceID]['ble.cloudrelay.list'] = { addrs: res.data.addrs };
					return lists;
				});
			}
		} catch (e) {
			console.error('Failed to fetch cloud relay list', e);
		}
	};

	if (isOnline && isFwAfter200(settings)) {
		fetchCloudRelayList();
	}
</script>

{#if !enable}
	<Alert
		heading={$format('title_bluetooth_gateway', {
			default: 'Bluetooth Gateway',
		})}
		isHeadingBold
		subHeading={isBeforeOneFive
			? $format('text_activate_gateway', {
					default:
						'You need to enable Bluetooth and the Gateway feature to use Bluetooth devices',
				})
			: ''}
		type={'warning'}
		cta={{
			title: $format('alert_title_virtual_components_go_to_blu_settings', {
				default: 'Go to Bluetooth settings',
			}),
		}}
		on:cta_click={() => navigateToSetting(device.id, 'bluetooth', 'bluetooth_settings')}
	/>
{/if}

{#if showAssignedDevices}
	<!-- {#if enable && (isObserver || isGatewayAlwaysEnabled)}
		<div class="d-flex justify-content-between mt-m mb-m align-items-center">
			<Text styleType="h2"
				>{$format('tab_subtitle_assigned_devices', {
					default: 'Assigned Bluetooth Devices',
				})}
			</Text>
		</div>
	{/if} -->

	{#if connectedDevices.length > 0}
		{#if isBeforeOneFive && !enable}
			<Alert
				class="mb-s"
				heading={$format('title_bluetooth_gateway', {
					default: 'Bluetooth Gateway',
				})}
				isHeadingBold
				subHeading={$format('text_activate_gateway', {
					default:
						'You need to enable Bluetooth and the Gateway feature to use Bluetooth devices',
				})}
				type={'warning'}
			/>
		{/if}
		{#if !isOnline}
			<Alert
				class="mb-s"
				heading={$format('title_device_status', {
					default: 'Device status',
				})}
				isHeadingBold
				subHeading={$format('text_device_offline_not_enabled', {
					default: 'Your device is currently offline',
				})}
			/>
		{/if}
		{#each connectedDevices as item, i}
			{#if isProxyingAvailable}
				<AdvancedCard
					isFocusable={typeof item !== 'string'}
					class={i < connectedDevices.length - 1 ? 'mb-s' : ''}
					title={typeof item !== 'string'
						? item?.name
						: $format('title_unknown_ble_device', {
								default: 'Unknown BLE Device',
							})}
					subtitle={{
						type: 'secondary',
						bold: false,
						textSmall: true,
						value:
							typeof item === 'string'
								? item
								: $rooms[item.room_id]?.name
									? $rooms[item.room_id]?.name
									: capFirstChar($format('label_hidden', { default: 'Hidden' })),
					}}
					avatar={getDeviceAvatar(item)}
					on:click={() => {
						if (typeof item !== 'string') {
							push(`/home/room/${item.room_id}/devices`);

							setSidePanelState({
								type: 'device',
								id: item.id,
								open: true,
							});
						}
					}}
				>
					<div class="d-flex align-items-center" slot="right">
						<IconButton
							slot="second"
							icon="fas fa-trash"
							variant="danger"
							disabled={!isOnline}
							aria-label={capFirstChar(
								$format('button_delete_device', { default: 'delete device' }),
							)}
							on:click={(e) => handleDeleteDevice(e, item)}
						/>
					</div>
				</AdvancedCard>
			{/if}
		{/each}
	{:else}
		<Alert
			type="info"
			heading={$format('no_devices_yet_heading', {
				default: 'No devices yet',
			})}
		/>
	{/if}

	{#if enable && (isObserver || isGatewayAlwaysEnabled) && isProxyingAvailable}
		<Button
			class="width-full mt-s"
			variant="primary"
			slot="second"
			size="m"
			on:click={() => {
				showAddModal = true;
			}}
		>
			{$format('text_add_new__bluetooth_device', {
				default: 'Add BLE device',
			})}
		</Button>
	{/if}
{/if}

<Modal
	showModal={showAddModal}
	on:close={closeAddModal}
	title={$format('title_add_bluetooth_device', {
		default: 'Add Bluetooth Device',
	})}
>
	<div slot="content">
		{#if availableBleDevices.length > 0}
			<Container noScroll>
				<SelectList
					title={$format('label_selected', {
						default: 'Selected:',
					})}
					titleHighlight={selectedDevicesCount}
					{list}
					on:click={handleDeviceSelection}
				/>
			</Container>
		{:else}
			<Alert
				type="info"
				message={$format('no_available_ble_devices', {
					default:
						"No available BLE devices found in your account that aren't already attached to this gateway.",
				})}
			/>
		{/if}
	</div>

	<div slot="footer" class="d-flex justify-content-end width-full">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{ sm: 'm' }}
			variant="secondary"
			on:click={closeAddModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="primary"
			responsiveSize={{ sm: 'm' }}
			disabled={selectedDevicesCount === 0}
			on:click={addSelectedDevices}
		>
			{$format('button_add_selected', {
				default: 'Add Selected ({count})',
				values: { count: selectedDevicesCount },
			})}
		</Button>
	</div>
</Modal>

<Modal {showModal} on:close={closeRemoveModal} title={$format('button_delete', { default: 'Delete' })}
	><svelte:fragment slot="content">
		<Container>
			<Text>
				{$format('on_delete_msg', {
					values: {
						this_type: deviceName,
					},
					default: 'Are you sure you want to delete {this_type}?',
				})}
			</Text>
			<Alert
				class="mt-m"
				type="warning"
				heading={$format('require_device_reboot_prompt', {
					default: 'Note that this action might require a device reboot.',
				})}
			/>
		</Container>
	</svelte:fragment>
	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={closeRemoveModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={removeDevice}
		>
			{$format('button_delete', { default: 'Delete' })}
		</Button>
	</div>
</Modal>
